import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { KITCHEN_HOLD_MINUTE_OPTIONS } from "../../../../lib/kitchenHold";
import type { KitchenHoldTime } from "./kitchenHoldItems";

/** Изборите в реда им: „Сега", минутите, „Ръчно" (∞). */
const HOLD_TIME_OPTIONS: KitchenHoldTime[] = ["now", ...KITCHEN_HOLD_MINUTE_OPTIONS, "manual"];

/** „Сега" / „10′" — думата на времето; „Ръчно" се рисува с `InfinityIcon`. */
// eslint-disable-next-line react-refresh/only-export-components
export const kitchenHoldTimeLabel = (time: KitchenHoldTime, t: TFunction): string =>
  time === "now"
    ? t("ordering.kitchenHold.now")
    : time === "manual"
      ? t("ordering.kitchenHold.manualShort")
      : t("ordering.kitchenHold.minutesShort", { count: time });

/**
 * Знакът за безкрайност — „Ръчно": чака, докато някой не натисне „Пусни
 * сега". Тънка линия, а не буквата ∞: в шрифта тя е удебелена и седи под
 * средата на реда, а иконата стои точно в средата. Примките са високи
 * (около половината от ширината), за да не изглежда смачкан в малките
 * бутони. Размерът — от `className` (ширината; височината следва
 * пропорцията 12:7), цветът — `currentColor`.
 */
export const InfinityIcon = ({ className = "w-[18px]" }: { className?: string }) => (
  <svg
    viewBox="0 0 24 14"
    aria-hidden
    data-infinity
    className={`inline-block shrink-0 aspect-[12/7] ${className}`}
  >
    <path
      d="M12 7 C 9.4 1.2, 3 1.2, 3 7 C 3 12.8, 9.4 12.8, 12 7 C 14.6 1.2, 21 1.2, 21 7 C 21 12.8, 14.6 12.8, 12 7 Z"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
  </svg>
);

/** Времето така, както го показват бутоните и чиповете: думата или ∞
 *  (`infinityClassName` — размерът на иконата). Екранният четец чете
 *  `aria-label` на бутона/чипа, не това. */
export const KitchenHoldTimeText = ({
  time,
  infinityClassName,
}: {
  time: KitchenHoldTime;
  infinityClassName?: string;
}) => {
  const { t } = useTranslation();
  return time === "manual" ? (
    <InfinityIcon className={infinityClassName} />
  ) : (
    <>{kitchenHoldTimeLabel(time, t)}</>
  );
};

/**
 * Седемте избора на време в равни колони — под заглавието („Едно време за
 * всички редове"), под реда и в „Промени" на задържана партида в масата.
 * Избраният е плътен акцент; `selected` null — никой (редовете са с различни
 * времена). В тесния панел (1024px — ~29px на колона) „Сега" и ∞ пазят
 * ширината си, а минутите се свиват.
 */
export const KitchenHoldTimeOptions = ({
  selected,
  onPick,
}: {
  selected: KitchenHoldTime | null;
  onPick: (time: KitchenHoldTime) => void;
}) => {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-[minmax(max-content,1fr)_repeat(5,minmax(0,1fr))_minmax(max-content,1fr)] gap-1">
      {HOLD_TIME_OPTIONS.map((time) => {
        const isSelected = time === selected;
        return (
          <button
            key={time}
            type="button"
            onClick={() => onPick(time)}
            aria-pressed={isSelected}
            aria-label={kitchenHoldTimeLabel(time, t)}
            title={time === "manual" ? t("ordering.kitchenHold.manualHint") : undefined}
            className={`h-9 min-w-0 rounded-[9px] border px-0.5 inline-flex items-center justify-center text-[12px] font-semibold whitespace-nowrap cursor-pointer transition-colors ${
              isSelected
                ? "bg-pos-accent border-pos-accent text-white"
                : "bg-pos-card border-pos-line3 text-pos-ink2 hover:bg-pos-panel"
            }`}
          >
            <KitchenHoldTimeText time={time} infinityClassName="w-5" />
          </button>
        );
      })}
    </div>
  );
};
